import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Wizard from "@/pages/Wizard";
import Library from "@/pages/Library";
import Sequences from "@/pages/Sequences";
import TacticCutter from "@/pages/TacticCutter";
import Settings from "@/pages/Settings";
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import KeyboardCheatSheetModal from "@/components/KeyboardCheatSheetModal";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import "@/App.css";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

function AdminApp({ children }) {
  return <Layout>{children}</Layout>;
}

function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background text-foreground">
        <Loader2 className="w-6 h-6 animate-spin text-primary mb-2" />
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Checking session…
        </div>
      </div>
    );
  }
  if (!user) return <Landing rejected={false} />;
  if (!user.is_admin) return <Landing rejected={true} />;
  return <Navigate to="/admin" replace />;
}

function AdminGuard({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background text-foreground">
        <Loader2 className="w-6 h-6 animate-spin text-primary mb-2" />
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  if (!user.is_admin) return <Landing rejected={true} />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_token=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      <Route path="/admin" element={<AdminGuard><AdminApp><Dashboard /></AdminApp></AdminGuard>} />
      <Route path="/admin/wizard" element={<AdminGuard><AdminApp><Wizard /></AdminApp></AdminGuard>} />
      <Route path="/admin/wizard/:id" element={<AdminGuard><AdminApp><Wizard /></AdminApp></AdminGuard>} />
      <Route path="/admin/library" element={<AdminGuard><AdminApp><Library /></AdminApp></AdminGuard>} />
      <Route path="/admin/sequences" element={<AdminGuard><AdminApp><Sequences /></AdminApp></AdminGuard>} />
      <Route path="/admin/tactics" element={<AdminGuard><AdminApp><TacticCutter /></AdminApp></AdminGuard>} />
      <Route path="/admin/settings" element={<AdminGuard><AdminApp><Settings /></AdminApp></AdminGuard>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <KeyboardCheatSheetModal />
      </AuthProvider>
    </BrowserRouter>
  );
}
